import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyPoundIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  HeartIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  StarIcon,
  CogIcon,
  ClockIcon,
  BanknotesIcon,
  IdentificationIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { employeeAPI, departmentsAPI, organizationAPI, probationaryAPI } from '../../services/api';
import DocumentManagement from './DocumentManagement';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeEditModal = ({ employee, onClose, onSave }) => {
  const { user } = useAuth();
  const userRole = user?.role;
  const [activeTab, setActiveTab] = useState('personal');
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [probationSettings, setProbationSettings] = useState({
    duration_months: 6,
    available_probation_periods: [3, 6, 9, 12]
  });

  const tabs = [
    { id: 'personal', name: 'Personal', icon: UserIcon },
    { id: 'employment', name: 'Employment', icon: BriefcaseIcon },
    { id: 'contact', name: 'Contact', icon: PhoneIcon },
    { id: 'skills', name: 'Skills & Education', icon: AcademicCapIcon },
    { id: 'payroll', name: 'Payroll & Benefits', icon: CurrencyPoundIcon },
    { id: 'performance', name: 'Performance', icon: StarIcon },
    { id: 'health', name: 'Health & Safety', icon: HeartIcon },
    { id: 'documents', name: 'Documents', icon: DocumentTextIcon }
  ];

  useEffect(() => {
    // Load departments, companies, and divisions
    const loadData = async () => {
      try {
        const [departmentsRes, companiesRes, probationRes] = await Promise.all([
          departmentsAPI.getActive(),
          organizationAPI.getCompanies(),
          probationaryAPI.getSettings()
        ]);
        setDepartments(departmentsRes.data.departments || []);
        setCompanies(companiesRes.data.companies || []);
        
        // Load probation settings
        const responseData = probationRes.data.data || probationRes.data;
        const probationDefaults = responseData?.probationary_period_defaults || {};
        const availablePeriods = probationDefaults.available_probation_periods || [3, 6, 9, 12];
        console.log('Loading probation settings in EditModal:', { probationDefaults, availablePeriods });
        setProbationSettings({
          duration_months: probationDefaults.duration_months || 6,
          available_probation_periods: availablePeriods
        });
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
    
    // Load divisions if employee already has a company
    if (employee?.company_id) {
      const loadInitialDivisions = async () => {
        try {
          const response = await organizationAPI.getDivisions({ company_id: employee.company_id });
          setDivisions(response.data.divisions || []);
        } catch (error) {
          console.error('Error loading initial divisions:', error);
        }
      };
      loadInitialDivisions();
    }
    
    if (employee) {
      setFormData({
        // Personal Information
        employee_number: employee.employee_number || '',
        title: employee.title || '',
        first_name: employee.first_name || '',
        middle_name: employee.middle_name || '',
        last_name: employee.last_name || '',
        preferred_name: employee.preferred_name || '',
        date_of_birth: employee.date_of_birth ? employee.date_of_birth.split('T')[0] : '',
        gender: employee.gender || '',
        marital_status: employee.marital_status || '',
        
        // Legal Information
        national_insurance_number: employee.national_insurance_number || '',
        passport_number: employee.passport_number || '',
        passport_expiry: employee.passport_expiry ? employee.passport_expiry.split('T')[0] : '',
        driving_license_number: employee.driving_license_number || '',
        driving_license_expiry: employee.driving_license_expiry ? employee.driving_license_expiry.split('T')[0] : '',
        right_to_work: employee.right_to_work || false,
        right_to_work_expiry: employee.right_to_work_expiry ? employee.right_to_work_expiry.split('T')[0] : '',
        visa_type: employee.visa_type || '',
        visa_expiry_date: employee.visa_expiry_date ? employee.visa_expiry_date.split('T')[0] : '',
        
        // Employment Details
        job_title: employee.job_title || '',
        company_id: employee.company_id || '',
        department: employee.department || '',
        division_id: employee.division_id || '',
        location: employee.location || '',
        work_location_type: employee.work_location_type || '',
        employment_category: employee.employment_category || '',
        employment_status: employee.employment_status || '',
        employment_type: employee.employment_type || '',
        
        // Employment Dates
        hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
        probation_months: employee.probation_months || 6,
        probation_end_date: employee.probation_end_date ? employee.probation_end_date.split('T')[0] : '',
        confirmation_date: employee.confirmation_date ? employee.confirmation_date.split('T')[0] : '',
        last_salary_review: employee.last_salary_review ? employee.last_salary_review.split('T')[0] : '',
        next_salary_review: employee.next_salary_review ? employee.next_salary_review.split('T')[0] : '',
        
        // Work Schedule
        working_hours_per_week: employee.working_hours_per_week || '',
        working_days_per_week: employee.working_days_per_week || '',
        start_time: employee.start_time || '',
        end_time: employee.end_time || '',
        break_duration_minutes: employee.break_duration_minutes || '',
        flexible_working: employee.flexible_working || false,
        
        // Contact Information
        personal_email: employee.personal_email || '',
        work_email: employee.work_email || '',
        phone: employee.phone || '',
        mobile: employee.mobile || '',
        work_phone: employee.work_phone || '',
        work_mobile: employee.work_mobile || '',
        
        // Address Information
        address_line1: employee.address_line1 || '',
        address_line2: employee.address_line2 || '',
        city: employee.city || '',
        county: employee.county || '',
        postcode: employee.postcode || '',
        country: employee.country || 'United Kingdom',
        
        // Emergency Contact
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        emergency_contact_mobile: employee.emergency_contact_mobile || '',
        emergency_contact_email: employee.emergency_contact_email || '',
        emergency_contact_relationship: employee.emergency_contact_relationship || '',
        
        // Salary Information
        salary_amount: employee.salary_amount || '',
        salary_frequency: employee.salary_frequency || '',
        hourly_rate: employee.hourly_rate || '',
        currency: employee.currency || 'GBP',
        bonus_eligible: employee.bonus_eligible || false,
        commission_eligible: employee.commission_eligible || false,
        
        // Bank Details
        bank_name: employee.bank_name || '',
        account_holder_name: employee.account_holder_name || '',
        sort_code: employee.sort_code || '',
        account_number: employee.account_number || '',
        
        // Tax & Pension
        tax_code: employee.tax_code || '',
        pension_scheme: employee.pension_scheme || false,
        pension_provider: employee.pension_provider || '',
        student_loan_plan: employee.student_loan_plan || '',
        
        // Skills & Education
        highest_qualification: employee.highest_qualification || '',
        qualification_institution: employee.qualification_institution || '',
        qualification_year: employee.qualification_year || '',
        professional_memberships: employee.professional_memberships || '',
        languages_spoken: employee.languages_spoken || '',
        technical_skills: employee.technical_skills || '',
        soft_skills: employee.soft_skills || '',
        
        // Performance & Development
        performance_rating: employee.performance_rating || '',
        last_performance_review: employee.last_performance_review ? employee.last_performance_review.split('T')[0] : '',
        next_performance_review: employee.next_performance_review ? employee.next_performance_review.split('T')[0] : '',
        career_goals: employee.career_goals || '',
        training_needs: employee.training_needs || '',
        development_plan: employee.development_plan || '',
        
        // Health & Safety
        health_conditions: employee.health_conditions || '',
        allergies: employee.allergies || '',
        medical_emergency_contact: employee.medical_emergency_contact || '',
        health_and_safety_training_completed: employee.health_and_safety_training_completed || false,
        last_health_assessment: employee.last_health_assessment ? employee.last_health_assessment.split('T')[0] : '',
        next_health_assessment: employee.next_health_assessment ? employee.next_health_assessment.split('T')[0] : '',
        
        // Additional Information
        hobbies_interests: employee.hobbies_interests || '',
        volunteer_work: employee.volunteer_work || '',
        awards_recognitions: employee.awards_recognitions || ''
      });
    }
  }, [employee]);

  const handleInputChange = async (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Load divisions when company is selected
    if (name === 'company_id' && value) {
      try {
        const response = await organizationAPI.getDivisions({ company_id: value });
        setDivisions(response.data.divisions || []);
        // Clear division and location when company changes
        setFormData(prev => ({
          ...prev,
          division_id: '',
          location: ''
        }));
      } catch (error) {
        console.error('Error loading divisions:', error);
      }
    }

    // Auto-fill location when division is selected
    if (name === 'division_id' && value) {
      const selectedDivision = divisions.find(div => div.id === value);
      if (selectedDivision && selectedDivision.location) {
        setFormData(prev => ({
          ...prev,
          location: selectedDivision.location
        }));
      }
    }

    // Auto-fill location from department if division is not selected
    if (name === 'department' && value && !formData.division_id) {
      const selectedDepartment = departments.find(dept => dept.name === value);
      if (selectedDepartment && selectedDepartment.location) {
        setFormData(prev => ({
          ...prev,
          location: selectedDepartment.location
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.first_name?.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.employee_number?.trim()) newErrors.employee_number = 'Employee number is required';
    if (!formData.hire_date) newErrors.hire_date = 'Hire date is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.personal_email && !emailRegex.test(formData.personal_email)) {
      newErrors.personal_email = 'Invalid email format';
    }
    if (formData.work_email && !emailRegex.test(formData.work_email)) {
      newErrors.work_email = 'Invalid email format';
    }
    
    // Phone validation
    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    if (formData.mobile && !phoneRegex.test(formData.mobile)) {
      newErrors.mobile = 'Invalid mobile format';
    }
    
    // Postcode validation (UK format)
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
    if (formData.postcode && !postcodeRegex.test(formData.postcode)) {
      newErrors.postcode = 'Invalid UK postcode format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      alert("Please fix the validation errors before saving.");
      return;
    }
    
    setLoading(true);
    try {
      console.log("Raw form data:", formData);
      
      // Filter out empty strings, null, and undefined values
      const cleanData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        if (value !== null && value !== undefined && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      console.log("Cleaned data to send:", cleanData);
      console.log("Updating employee:", employee.id);
      
      const token = localStorage.getItem("authToken");
      console.log("Auth token available:", !!token);
      
      const result = await employeeAPI.update(employee.id, cleanData);
      console.log("Update successful:", result);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error updating employee:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Handle server-side validation errors
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          // Map server error field to form field
          const field = err.path || err.param;
          if (field) {
            serverErrors[field] = err.msg || err.message;
          }
        });
        setErrors(prevErrors => ({
          ...prevErrors,
          ...serverErrors
        }));
        
        const errorFields = Object.keys(serverErrors);
        if (errorFields.length > 0) {
          alert(`Please fix the following errors:\n• ${Object.values(serverErrors).join('\n• ')}`);
        } else {
          alert("Validation failed. Please check your inputs and try again.");
        }
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Failed to update employee. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (name, label, type = 'text', options = null, required = false) => (
    <div className="form-group">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'select' ? (
        <select
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          className={`form-select mt-1 ${errors[name] ? 'border-red-300' : ''}`}
        >
          <option value="">Select {label}</option>
          {options?.map((option, index) => (
            typeof option === 'object' ? (
              <option key={option.value || index} value={option.value}>
                {option.label}
              </option>
            ) : (
              <option key={option} value={option}>{option}</option>
            )
          ))}
        </select>
      ) : type === 'checkbox' ? (
        <div className="mt-1">
          <label className="flex items-center">
            <input
              type="checkbox"
              name={name}
              checked={formData[name] || false}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-600">{label}</span>
          </label>
        </div>
      ) : type === 'textarea' ? (
        <textarea
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          rows={3}
          className={`form-textarea mt-1 ${errors[name] ? 'border-red-300' : ''}`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          className={`form-input mt-1 ${errors[name] ? 'border-red-300' : ''}`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      )}
      {errors[name] && (
        <p className="mt-1 text-sm text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  if (!employee) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <div className="flex justify-between items-center w-full">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-semibold">
                  {formData.first_name?.charAt(0) || 'E'}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Edit Employee: {formData.first_name} {formData.last_name}
                </h2>
                <p className="text-gray-600">{formData.job_title || 'No job title'}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                <CheckIcon className="w-4 h-4" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
              <button
                onClick={onClose}
                className="btn-secondary flex items-center space-x-2"
              >
                <XCircleIcon className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 px-6">
            <nav className="-mb-px flex flex-wrap gap-2 md:gap-4 lg:gap-6 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-3 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Form Content */}
          <div className="modal-body">
            {/* Personal Information */}
            {activeTab === 'personal' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <UserIcon className="w-5 h-5 mr-2" />
                    Basic Information
                  </h3>
                  {renderInput('employee_number', 'Employee Number', 'text', null, true)}
                  {renderInput('title', 'Title', 'select', ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof'])}
                  {renderInput('first_name', 'First Name', 'text', null, true)}
                  {renderInput('middle_name', 'Middle Name')}
                  {renderInput('last_name', 'Last Name', 'text', null, true)}
                  {renderInput('preferred_name', 'Preferred Name')}
                  {renderInput('date_of_birth', 'Date of Birth', 'date')}
                  {renderInput('gender', 'Gender', 'select', ['Male', 'Female', 'Other', 'Prefer not to say'])}
                  {renderInput('marital_status', 'Marital Status', 'select', ['Single', 'Married', 'Divorced', 'Widowed', 'Civil Partnership'])}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <IdentificationIcon className="w-5 h-5 mr-2" />
                    Legal Information
                  </h3>
                  {renderInput('national_insurance_number', 'National Insurance Number')}
                  <div className="grid grid-cols-2 gap-4">
                    {renderInput('passport_number', 'Passport Number')}
                    {renderInput('passport_expiry', 'Passport Expiry', 'date')}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {renderInput('driving_license_number', 'Driving License')}
                    {renderInput('driving_license_expiry', 'Driving License Expiry', 'date')}
                  </div>
                  {renderInput('right_to_work', 'Right to Work in UK', 'checkbox')}
                  {renderInput('right_to_work_expiry', 'Right to Work Expiry', 'date')}
                  <div className="grid grid-cols-2 gap-4">
                    {renderInput('visa_type', 'Visa Type')}
                    {renderInput('visa_expiry_date', 'Visa Expiry Date', 'date')}
                  </div>

                  {/* Document Status Summary */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                      <DocumentTextIcon className="w-4 h-4 mr-2" />
                      Document Status Summary
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {formData.visa_type && (
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700">Visa ({formData.visa_type})</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            formData.visa_expiry_date 
                              ? new Date(formData.visa_expiry_date) < new Date() 
                                ? 'bg-red-100 text-red-800'
                                : new Date(formData.visa_expiry_date) < new Date(Date.now() + 30*24*60*60*1000)
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {formData.visa_expiry_date 
                              ? new Date(formData.visa_expiry_date) < new Date() 
                                ? 'Expired'
                                : new Date(formData.visa_expiry_date).toLocaleDateString()
                              : 'No expiry date'
                            }
                          </span>
                        </div>
                      )}
                      {formData.right_to_work_expiry && (
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700">Right to Work</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            new Date(formData.right_to_work_expiry) < new Date() 
                              ? 'bg-red-100 text-red-800'
                              : new Date(formData.right_to_work_expiry) < new Date(Date.now() + 30*24*60*60*1000)
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {new Date(formData.right_to_work_expiry) < new Date() 
                              ? 'Expired'
                              : new Date(formData.right_to_work_expiry).toLocaleDateString()
                            }
                          </span>
                        </div>
                      )}
                      {formData.driving_license_expiry && (
                        <div className="flex justify-between items-center">
                          <span className="text-blue-700">Driving License</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            new Date(formData.driving_license_expiry) < new Date() 
                              ? 'bg-red-100 text-red-800'
                              : new Date(formData.driving_license_expiry) < new Date(Date.now() + 30*24*60*60*1000)
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {new Date(formData.driving_license_expiry) < new Date() 
                              ? 'Expired'
                              : new Date(formData.driving_license_expiry).toLocaleDateString()
                            }
                          </span>
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-xs text-blue-600">
                          💡 Visit the "Documents" tab to upload and manage document files
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Employment Information */}
            {activeTab === 'employment' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <BriefcaseIcon className="w-5 h-5 mr-2" />
                    Employment Details
                  </h3>
                  {renderInput('job_title', 'Job Title')}
                  {renderInput('company_id', 'Company', 'select', 
                    companies && companies.length > 0 
                      ? companies.map(company => ({
                          value: company.id,
                          label: `${company.name}${company.code ? ` (${company.code})` : ''}`
                        }))
                      : [{ value: '', label: 'Loading companies...' }]
                  )}
                  {renderInput('department', 'Department', 'select', 
                    departments && departments.length > 0 
                      ? departments.map(dept => ({
                          value: dept.name,
                          label: `${dept.name}${dept.code ? ` (${dept.code})` : ''}`
                        }))
                      : [{ value: '', label: 'Loading departments...' }]
                  )}
                  {renderInput('division_id', 'Division', 'select', 
                    divisions && divisions.length > 0 
                      ? divisions.map(division => ({
                          value: division.id,
                          label: `${division.name}${division.code ? ` (${division.code})` : ''}`
                        }))
                      : [{ value: '', label: formData.company_id ? 'Loading divisions...' : 'Select company first' }]
                  )}
                  {renderInput('location', 'Location')}
                  {renderInput('work_location_type', 'Work Location Type', 'select', ['Office', 'Remote', 'Hybrid', 'Field'])}
                  {renderInput('employment_category', 'Employment Category', 'select', ['Permanent', 'Fixed Term', 'Casual', 'Agency', 'Consultant'])}
                  {renderInput('employment_status', 'Employment Status', 'select', ['active', 'inactive', 'terminated', 'suspended', 'on_leave'])}
                  {renderInput('employment_type', 'Employment Type', 'select', ['full-time', 'part-time', 'contract', 'temporary'])}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <CalendarIcon className="w-5 h-5 mr-2" />
                    Employment Dates
                  </h3>
                  {renderInput('hire_date', 'Hire Date', 'date', null, true)}
                  {['admin', 'hr_manager'].includes(userRole) && (
                    <div className="form-group">
                      <label htmlFor="probation_months" className="form-label">
                        Probation Period (Months) <span className="required">*</span>
                      </label>
                      <select
                        id="probation_months"
                        name="probation_months"
                        value={formData.probation_months}
                        onChange={handleInputChange}
                        className={`form-input ${errors.probation_months ? 'error' : ''}`}
                        required
                      >
                        {probationSettings.available_probation_periods.map(period => (
                          <option key={period} value={period}>
                            {period} Month{period !== 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                      {errors.probation_months && <span className="error-message">{errors.probation_months}</span>}
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="probation_end_date" className="form-label">
                      Probation End Date (Calculated automatically)
                    </label>
                    <input
                      type="date"
                      id="probation_end_date"
                      name="probation_end_date"
                      value={formData.probation_end_date}
                      className="form-input"
                      readOnly
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small className="form-text">This date is calculated automatically based on hire date and probation months</small>
                  </div>
                  {renderInput('confirmation_date', 'Confirmation Date', 'date')}
                  {renderInput('last_salary_review', 'Last Salary Review', 'date')}
                  {renderInput('next_salary_review', 'Next Salary Review', 'date')}
                  
                  <h3 className="text-lg font-medium text-gray-900 flex items-center mt-6">
                    <ClockIcon className="w-5 h-5 mr-2" />
                    Work Schedule
                  </h3>
                  {renderInput('working_hours_per_week', 'Working Hours per Week', 'number')}
                  {renderInput('working_days_per_week', 'Working Days per Week', 'number')}
                  {renderInput('start_time', 'Start Time', 'time')}
                  {renderInput('end_time', 'End Time', 'time')}
                  {renderInput('break_duration_minutes', 'Break Duration (minutes)', 'number')}
                  {renderInput('flexible_working', 'Flexible Working', 'checkbox')}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {activeTab === 'contact' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <PhoneIcon className="w-5 h-5 mr-2" />
                    Contact Details
                  </h3>
                  {renderInput('personal_email', 'Personal Email', 'email')}
                  {renderInput('work_email', 'Work Email', 'email')}
                  {renderInput('phone', 'Phone')}
                  {renderInput('mobile', 'Mobile')}
                  {renderInput('work_phone', 'Work Phone')}
                  {renderInput('work_mobile', 'Work Mobile')}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-2" />
                    Address Information
                  </h3>
                  {renderInput('address_line1', 'Address Line 1')}
                  {renderInput('address_line2', 'Address Line 2')}
                  {renderInput('city', 'City')}
                  {renderInput('county', 'County')}
                  {renderInput('postcode', 'Postcode')}
                  {renderInput('country', 'Country')}
                  
                  <h3 className="text-lg font-medium text-gray-900 flex items-center mt-6">
                    <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                    Emergency Contact
                  </h3>
                  {renderInput('emergency_contact_name', 'Name')}
                  {renderInput('emergency_contact_phone', 'Phone')}
                  {renderInput('emergency_contact_mobile', 'Mobile')}
                  {renderInput('emergency_contact_email', 'Email', 'email')}
                  {renderInput('emergency_contact_relationship', 'Relationship')}
                </div>
              </div>
            )}

            {/* Skills & Education */}
            {activeTab === 'skills' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <AcademicCapIcon className="w-5 h-5 mr-2" />
                    Education & Qualifications
                  </h3>
                  {renderInput('highest_qualification', 'Highest Qualification')}
                  {renderInput('qualification_institution', 'Institution')}
                  {renderInput('qualification_year', 'Year', 'number')}
                  {renderInput('professional_memberships', 'Professional Memberships', 'textarea')}
                  {renderInput('languages_spoken', 'Languages Spoken', 'textarea')}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <CogIcon className="w-5 h-5 mr-2" />
                    Skills
                  </h3>
                  {renderInput('technical_skills', 'Technical Skills', 'textarea')}
                  {renderInput('soft_skills', 'Soft Skills', 'textarea')}
                  {renderInput('hobbies_interests', 'Hobbies & Interests', 'textarea')}
                  {renderInput('volunteer_work', 'Volunteer Work', 'textarea')}
                  {renderInput('awards_recognitions', 'Awards & Recognitions', 'textarea')}
                </div>
              </div>
            )}

            {/* Payroll & Benefits */}
            {activeTab === 'payroll' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <CurrencyPoundIcon className="w-5 h-5 mr-2" />
                    Salary Information
                  </h3>
                  {renderInput('salary_amount', 'Salary Amount', 'number')}
                  {renderInput('salary_frequency', 'Salary Frequency', 'select', ['monthly', 'weekly', 'bi-weekly', 'annual'])}
                  {renderInput('hourly_rate', 'Hourly Rate', 'number')}
                  {renderInput('currency', 'Currency', 'select', ['GBP', 'USD', 'EUR'])}
                  {renderInput('bonus_eligible', 'Bonus Eligible', 'checkbox')}
                  {renderInput('commission_eligible', 'Commission Eligible', 'checkbox')}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <BanknotesIcon className="w-5 h-5 mr-2" />
                    Bank Details
                  </h3>
                  {renderInput('bank_name', 'Bank Name')}
                  {renderInput('account_holder_name', 'Account Holder Name')}
                  {renderInput('sort_code', 'Sort Code')}
                  {renderInput('account_number', 'Account Number')}
                  
                  <h3 className="text-lg font-medium text-gray-900 flex items-center mt-6">
                    <ShieldCheckIcon className="w-5 h-5 mr-2" />
                    Tax & Pension
                  </h3>
                  {renderInput('tax_code', 'Tax Code')}
                  {renderInput('pension_scheme', 'Pension Scheme', 'checkbox')}
                  {renderInput('pension_provider', 'Pension Provider')}
                  {renderInput('student_loan_plan', 'Student Loan Plan', 'select', ['Plan 1', 'Plan 2', 'Plan 4', 'Postgraduate Loan'])}
                </div>
              </div>
            )}

            {/* Performance */}
            {activeTab === 'performance' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <StarIcon className="w-5 h-5 mr-2" />
                    Performance Information
                  </h3>
                  {renderInput('performance_rating', 'Performance Rating', 'select', ['Outstanding', 'Exceeds Expectations', 'Meets Expectations', 'Below Expectations', 'Unsatisfactory'])}
                  {renderInput('last_performance_review', 'Last Performance Review', 'date')}
                  {renderInput('next_performance_review', 'Next Performance Review', 'date')}
                  {renderInput('career_goals', 'Career Goals', 'textarea')}
                  {renderInput('training_needs', 'Training Needs', 'textarea')}
                  {renderInput('development_plan', 'Development Plan', 'textarea')}
                </div>
              </div>
            )}

            {/* Health & Safety */}
            {activeTab === 'health' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <HeartIcon className="w-5 h-5 mr-2" />
                    Health Information
                  </h3>
                  {renderInput('health_conditions', 'Health Conditions', 'textarea')}
                  {renderInput('allergies', 'Allergies', 'textarea')}
                  {renderInput('medical_emergency_contact', 'Medical Emergency Contact')}
                  {renderInput('last_health_assessment', 'Last Health Assessment', 'date')}
                  {renderInput('next_health_assessment', 'Next Health Assessment', 'date')}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <ShieldCheckIcon className="w-5 h-5 mr-2" />
                    Safety Training
                  </h3>
                  {renderInput('health_and_safety_training_completed', 'Health & Safety Training Completed', 'checkbox')}
                </div>
              </div>
            )}

            {/* Documents */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                <DocumentManagement
                  employeeId={employee?.id}
                  employeeName={`${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()}
                  canEdit={true}
                  canViewAll={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeEditModal;
